
import Vue from 'vue';
import permissions from '@/lib/permissions';
import NavBar from '@/components/common/NavBar.vue';
import SideBar from '@/components/common/SideBar.vue';
import LoadingScreen from '@/components/common/LoadingScreen.vue';
import LogoEASSolutions from '@/assets/icons/logo/LogoEASSolutions.svg';
import LogoTempSense from '@/assets/icons/logo/tempsense.svg';
import Toast from '@/components/common/Toast.vue';
import { Client, Site } from '@/interfaces/multiTenancy';
import { getSupportedLocales } from '@/i18n/config/supportedLocales';
import { AnnotatedLocal } from '@/i18n/interfaces/locales';
import { ToastMessage } from '@/interfaces/alerts';
import { User } from '@/interfaces/userInfo';
import dayjs from '@/plugins/dayjs';

export default Vue.extend({
	components: {
		NavBar,
		SideBar,
		Toast,
		LoadingScreen,
		LogoEASSolutions,
		LogoTempSense,
	},

	data() {
		return {
			mobileWidth: 768,
			sideBarVisible: false,
		} as {
			mobileWidth: number;
			sideBarVisible: boolean;
		};
	},

	created() {
		if (innerWidth <= this.mobileWidth) {
			this.sideBarVisible = true;
		}
	},

	computed: {
		user(): User {
			return this.$store.getters['usermanagement/user/getUser'];
		},

		userAdmin(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole']);
		},

		version(): string | undefined {
			return process.env.VERSION;
		},

		isAuthenticated(): boolean {
			return this.$store.getters['usermanagement/authentication/getLoggedIn'];
		},

		loadingStatus(): boolean {
			return this.$store.getters['tools/loading/getLoadingStatus'];
		},

		activeLocale(): string {
			return this.$i18n.locale;
		},

		supportedLocales(): AnnotatedLocal[] {
			return getSupportedLocales();
		},

		selectedClient(): Client | null {
			return this.$store.getters['tenancy/clients/getSelectedClient'];
		},

		selectedSite(): Site | null {
			return this.$store.getters['tenancy/sites/getSelectedSite'];
		},

		currentRouteName() {
			return this.$route.name;
		},

		userClients(): Client[] {
			return this.$store.getters['tenancy/clients/getClients'];
		},

		clientSites(): Site[] {
			return this.$store.getters['tenancy/sites/getClientSites'](this.selectedClient?.id);
		},

		messages(): ToastMessage[] {
			return this.$store.getters['tools/messageHandling/getMessages'];
		},
	},

	methods: {
		navigation(name: string): void {
			if (innerWidth <= this.mobileWidth) {
				this.sideBarToggled();
			}

			if (name === 'admin') {
				window.location.href = `${window.location.origin}/admin`;
			}
		},

		sideBarToggleMobile(): void {
			if (innerWidth <= this.mobileWidth && !this.sideBarVisible) {
				this.sideBarToggled();
			}
		},

		logout(): void {
			this.$store.dispatch('usermanagement/authentication/logOut');
			if (this.$router.currentRoute.name !== 'Login') {
				this.$router.push({ name: 'Login' });
			}
		},

		sideBarToggled(): void {
			this.sideBarVisible = !this.sideBarVisible;
		},

		changeLocale(languageCode: string) {
			this.$i18n.locale = languageCode;
			this.$cookies.set('Language', { code: languageCode }, '3m');
			switch (languageCode) {
				case 'en-gb':
					dayjs.locale('en-gb');
					break;
				case 'fr':
					dayjs.locale('fr');
					break;
				default:
					dayjs.locale('en-gb');
					break;
			}
		},

		redirectToTempSense() {
			window.location.href = `${window.location.origin}/temp-sense`;
		},

		async tenancyChange(tenancyData: Client | Site, type: 'client' | 'site'): Promise<void> {
			if (type === 'client') {
				if ((tenancyData as Client).id === this.selectedClient!.id) {
					return;
				}
			} else {
				if ((tenancyData as Site).id === this.selectedSite!.id) {
					return;
				}
			}

			await this.$store.dispatch('tools/globalRefresh');
			await this.$store.dispatch('cleanSiteData');

			if (type === 'client') {
				await this.changeClient(tenancyData as Client);
			} else {
				await this.changeSite(tenancyData as Site);
			}
			await this.$store.dispatch('tools/globalRefresh');
		},

		async changeClient(client: Client): Promise<void> {
			await this.$store.dispatch('tenancy/clients/selectClient', { client: client });
			const clientSites = this.clientSites;
			if (clientSites && clientSites.length > 0) {
				await this.changeSite(clientSites[0]);
			} else {
				await this.$store.dispatch('tenancy/clients/selectClient', { client: client });
			}
		},

		async changeSite(site: Site): Promise<void> {
			await this.$store.dispatch('usermanagement/authentication/getSiteSpecificToken', {
				clientId: site.fk_client,
				siteId: site.id,
			});
			await this.$store.dispatch('tenancy/sites/selectSite', { site });
			await this.$store.dispatch('fetchAllData');
			this.$store.dispatch('usermanagement/user/setUserRole', {
				userRole: permissions.checkUserRole(
					this.user,
					this.$store.getters['types/roles/getRoles'],
					site
				),
			});
		},

		pickMessageTitle(variant: string): string {
			switch (variant) {
				case 'primary':
					return this.$t('Message').toString();
				case 'info':
					return this.$t('Info').toString();
				case 'success':
				case 'success-alt':
					return this.$t('Success').toString();
				case 'warning':
					return this.$t('Warning').toString();
				case 'danger':
					return this.$t('Failed').toString();
				default:
					return this.$t('Unknown').toString();
			}
		},

		removeMessage(id: string): void {
			this.$store.dispatch('tools/messageHandling/removeMessage', id);
		},
	},
});
